// JOH World — LEVEL 3: BAG MEMORY (heavy-bag hoek van de JOH Gym; geen vrije beweging).
import { PAL, VW, Particles, FloatTexts, pixelText } from "./engine";
import { drawGym } from "./gym";
import { drawFighter } from "./sprites";
import { sfx } from "./audio";

export const COMBOS = [
  [1, 2],
  [1, 1, 2],
  [1, 2, 1, 2],
  [2, 1, 2],
  [1, 2, 2, 1, 2],
];
const FLOOR_Y = 178;
const BAG_X = 250;
const PLAYER_X = 196;

export class Level3 {
  // cb: onScore(n), onRound(round), onComplete(attempts)
  constructor(cb) {
    this.cb = cb;
    this.t = 0;
    this.phase = "intro"; // intro → watch → input → retry → done
    this.phaseT = 0;
    this.round = 0; // index in COMBOS
    this.attempts = 0;
    this.inputIdx = 0;
    this.watchIdx = -1; // welke punch zichtbaar is tijdens watch
    this.bagSwing = 0;   // hoek van de bag
    this.bagVel = 0;
    this.playerAttack = null; // {kind, t, dur}
    this.particles = new Particles();
    this.texts = new FloatTexts();
    this.showSeq = false;
  }

  get combo() { return COMBOS[this.round]; }
  get watchDuration() { return 0.42 * this.combo.length + 0.75; } // 1.3s – 2.9s, langer = meer tijd

  press(num) {
    if (this.phase !== "input" || this.playerAttack) return;
    const kind = num === 1 ? "jab" : "cross";
    this.playerAttack = { kind, t: 0, dur: kind === "jab" ? 0.26 : 0.34 };
    sfx[kind]();
    // direct valideren
    if (this.combo[this.inputIdx] === num) {
      this.inputIdx += 1;
      this.cb.onScore?.(10);
      this._bagHit(kind);
      if (this.inputIdx >= this.combo.length) {
        this.cb.onScore?.(25);
        this.texts.add(PLAYER_X, 60, "CLEAN COMBO! +25", PAL.yellow, 9, 1.3);
        sfx.cleanCombo();
        this.cb.onRound?.(this.round + 1);
        this.phase = "comboDone";
        this.phaseT = 0;
      }
    } else {
      this.attempts += 1;
      this.phase = "retry";
      this.phaseT = 0;
      sfx.wrongCombo();
    }
  }

  _bagHit(kind) {
    this.bagVel += kind === "cross" ? 2.6 : 1.5; // cross beweegt de bag merkbaar meer
    sfx.bagHit();
    this.particles.burst(BAG_X - 14, 108, PAL.yellow, kind === "cross" ? 8 : 5, 50, 0.4, 2);
  }

  update(dt) {
    this.t += dt;
    this.phaseT += dt;
    this.particles.update(dt);
    this.texts.update(dt);
    if (this.playerAttack) {
      this.playerAttack.t += dt;
      if (this.playerAttack.t >= this.playerAttack.dur) this.playerAttack = null;
    }
    // bag-fysica (veer + demping)
    this.bagVel += -this.bagSwing * 26 * dt;
    this.bagVel *= 1 - 2.2 * dt;
    this.bagSwing += this.bagVel * dt;

    if (this.phase === "intro" && this.phaseT > 2.6) {
      this.phase = "watch";
      this.phaseT = 0;
      this.watchIdx = -1;
    } else if (this.phase === "watch") {
      const idx = Math.floor(this.phaseT / 0.46);
      if (idx < this.combo.length) {
        if (idx !== this.watchIdx) {
          this.watchIdx = idx;
          this._bagHit(this.combo[idx] === 1 ? "jab" : "cross"); // de bag "demonstreert"
        }
      } else if (this.phaseT > this.watchDuration) {
        this.phase = "input";
        this.phaseT = 0;
        this.inputIdx = 0;
      }
    } else if (this.phase === "retry" && this.phaseT > 1.5) {
      this.phase = "watch";
      this.phaseT = 0;
      this.watchIdx = -1;
    } else if (this.phase === "comboDone" && this.phaseT > 1.5) {
      if (this.round >= COMBOS.length - 1) {
        this.phase = "done";
        this.cb.onComplete?.(this.attempts + COMBOS.length);
      } else {
        this.round += 1;
        this.phase = "watch";
        this.phaseT = 0;
        this.watchIdx = -1;
      }
    }
  }

  draw(ctx) {
    drawGym(ctx, this.t, "bag");

    // ketting + bag
    const pivotX = BAG_X;
    const pivotY = 14;
    const bagLen = 78;
    const bx = pivotX + Math.sin(this.bagSwing) * bagLen;
    const by = pivotY + Math.cos(this.bagSwing) * bagLen;
    ctx.strokeStyle = PAL.metal;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(pivotX, pivotY);
    ctx.lineTo(bx, by - 26);
    ctx.stroke();
    ctx.fillStyle = PAL.darkRed;
    ctx.fillRect(Math.round(bx) - 13, Math.round(by) - 26, 26, 56);
    ctx.fillStyle = PAL.red;
    ctx.fillRect(Math.round(bx) - 13, Math.round(by) - 26, 26, 8);
    ctx.fillRect(Math.round(bx) - 13, Math.round(by) + 20, 26, 6);
    pixelText(ctx, "JOH", Math.round(bx), Math.round(by) - 6, 7, PAL.cream, "center");
    // schaduw
    ctx.fillStyle = "rgba(0,0,0,0.3)";
    ctx.beginPath();
    ctx.ellipse(bx, FLOOR_Y + 4, 18, 4, 0, 0, Math.PI * 2);
    ctx.fill();

    // speler (richting bag)
    let pose = "idle";
    if (this.playerAttack) pose = this.playerAttack.kind;
    if (this.phase === "done") pose = "victory";
    drawFighter(ctx, PLAYER_X, FLOOR_Y, 34, pose, this.t, {
      phase: this.playerAttack ? this.playerAttack.t / this.playerAttack.dur : 0,
    });

    this.particles.draw(ctx);
    this.texts.draw(ctx);

    // teksten per fase
    if (this.phase === "intro") {
      pixelText(ctx, "FINAL TEST", VW / 2, 34, 13, PAL.yellow, "center");
      pixelText(ctx, "BAG MEMORY", VW / 2, 56, 10, PAL.cream, "center");
      if (this.phaseT > 1.1) {
        pixelText(ctx, "WATCH THE COMBO.", VW / 2, 78, 7, PAL.cream, "center");
        pixelText(ctx, "REMEMBER IT. THEN HIT IT.", VW / 2, 90, 7, PAL.yellow, "center");
      }
    } else if (this.phase === "watch") {
      pixelText(ctx, `ROUND ${this.round + 1}/5 — WATCH`, VW / 2, 22, 8, PAL.cream, "center");
      // combo-iconen
      const n = this.combo.length;
      const w = n * 26;
      const x0 = VW / 2 - w / 2;
      for (let i = 0; i < n; i++) {
        const vis = i <= this.watchIdx;
        const cx = x0 + i * 26 + 10;
        ctx.fillStyle = vis ? (this.combo[i] === 1 ? PAL.cream : PAL.yellow) : "#3A3A41";
        ctx.fillRect(cx - 9, 40, 18, 18);
        if (vis) pixelText(ctx, String(this.combo[i]), cx, 45, 9, PAL.black, "center");
      }
    } else if (this.phase === "input") {
      pixelText(ctx, "YOUR TURN!", VW / 2, 22, 10, PAL.yellow, "center");
      // voortgangsdots (combinatie blijft verborgen)
      const n = this.combo.length;
      const w = n * 16;
      const x0 = VW / 2 - w / 2;
      for (let i = 0; i < n; i++) {
        ctx.fillStyle = i < this.inputIdx ? PAL.yellow : "#3A3A41";
        ctx.fillRect(x0 + i * 16, 42, 10, 6);
      }
    } else if (this.phase === "retry") {
      pixelText(ctx, "WRONG COMBO", VW / 2, 30, 12, PAL.red, "center");
      pixelText(ctx, "WATCH AGAIN", VW / 2, 52, 8, PAL.cream, "center");
    }
  }
}
